import React from 'react';
import FooterSection from './Homepage/components/FooterSection';

const Terms = () => {
  return (
    <>
      <main className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-14 flex-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
            Terms of Service
          </h1>
          <p className="text-sm md:text-base text-slate-600 mb-2">
            <strong>Last updated:</strong> December 7, 2025
          </p>

          <section className="space-y-4 mt-6">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              1. Agreement to Terms
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              These Terms of Service (&quot;Terms&quot;) constitute a legally
              binding agreement between you (&quot;User,&quot; &quot;Client,&quot;
              or &quot;Customer&quot;) and CleanBid.ai (&quot;Company,&quot;
              &quot;we,&quot; or &quot;us&quot;). By accessing or using the
              CleanBid.ai website, platform, or services, you agree to be bound by
              these Terms. If you do not agree, you must not use CleanBid.ai.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              2. Description of Service
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai provides a software-as-a-service (SaaS) platform that
              helps service businesses:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Generate and manage quotes</li>
              <li>Track leads and clients</li>
              <li>Send SMS and other communications</li>
              <li>Use AI-assisted workflows and automations</li>
              <li>Coordinate field service operations</li>
              <li>Access reporting and analytics tools</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              We may modify or improve the platform over time, including adding,
              changing, or removing features.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              3. Eligibility
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              To use CleanBid.ai you must:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Be at least 18 years old</li>
              <li>
                Have the legal authority to enter into a binding agreement on
                behalf of yourself or your organization
              </li>
              <li>Provide accurate and complete account information</li>
            </ul>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              4. User Accounts
            </h2>
            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              4.1 Account Creation
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              You may need to create an account to access certain features. You
              agree to provide accurate, current, and complete information during
              registration and to keep that information up to date.
            </p>
            <h3 className="text-sm md:text-base font-semibold text-slate-900">
              4.2 Account Security
            </h3>
            <p className="text-sm md:text-base text-slate-700">
              You are responsible for safeguarding your login credentials and for
              all activity that occurs under your account. If you suspect any
              unauthorized access, you must notify us immediately.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              5. Subscription, Fees, and Payment
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai may be offered under one or more subscription plans,
              which may include monthly or annual billing. By subscribing, you
              agree to pay all applicable fees as described at the time of
              purchase or in your order form.
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>
                Fees are typically billed through a third-party provider (e.g.,
                Stripe).
              </li>
              <li>
                Unless otherwise stated, all payments are non-refundable except as
                required by law.
              </li>
              <li>
                We may change fees upon reasonable notice for future billing
                cycles.
              </li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              You are responsible for any taxes associated with your use of the
              Service, excluding our income taxes.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              6. Cancellation and Termination
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              You may cancel your subscription at any time through your account
              settings or by contacting support. Your access will typically
              continue until the end of your current billing period.
            </p>
            <p className="text-sm md:text-base text-slate-700">
              We may suspend or terminate your access to CleanBid.ai if:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>You violate these Terms</li>
              <li>You misuse SMS or communication features</li>
              <li>You fail to pay applicable fees</li>
              <li>Your use creates risk or legal exposure for CleanBid.ai</li>
            </ul>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              7. Acceptable Use Policy
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              You agree not to use CleanBid.ai to:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Engage in unlawful, fraudulent, or deceptive activities</li>
              <li>Send spam or unsolicited communications</li>
              <li>Harass, threaten, or abuse others</li>
              <li>Upload malicious code or interfere with the platform</li>
              <li>Attempt to bypass security or authentication measures</li>
              <li>Reverse engineer or copy the platform&apos;s code or design</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              We reserve the right to investigate any potential violation and take
              appropriate action, including account suspension or termination.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              8. SMS Messaging &amp; Compliance
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai provides tools to send SMS and other communications to
              your contacts. By using these tools, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>
                You are responsible for obtaining all required consents from your
                contacts before sending messages.
              </li>
              <li>
                You will comply with all applicable laws and regulations,
                including but not limited to the Telephone Consumer Protection Act
                (TCPA), CAN-SPAM, CTIA guidelines, and local equivalents.
              </li>
              <li>
                Carriers and providers (such as Telnyx) may filter, block, or
                delay messages and we are not liable for such actions.
              </li>
              <li>
                You will honor opt-out requests and promptly stop sending messages
                to any contact who replies &quot;STOP&quot; or withdraws consent.
              </li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai is not responsible for penalties, fines, or damages
              arising from your misuse of messaging features or non-compliance
              with applicable regulations.
            </p>
            <p className="text-sm md:text-base text-slate-700">
              For recipients of CleanBid.ai messages: reply <strong>HELP</strong>{' '}
              for help or <strong>STOP</strong> to unsubscribe at any time.
              Message frequency varies. Message and data rates may apply. For
              assistance, contact{' '}
              <a href="mailto:info@cleanbid.ai" className="font-medium underline">
                info@cleanbid.ai
              </a>.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              9. Third-Party Services
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai integrates with third-party services such as:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Telnyx (for SMS and telephony)</li>
              <li>Google Cloud / Firebase (for hosting and infrastructure)</li>
              <li>Stripe (for payment processing)</li>
              <li>Other analytics and operational tools</li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              Your use of these third-party services may be subject to their own
              terms and privacy policies. We are not responsible for their actions
              or outages.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              10. Intellectual Property
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              All intellectual property rights in and to CleanBid.ai, including
              but not limited to software, code, design, branding, logos, and
              documentation, are owned by or licensed to CleanBid.ai.
            </p>
            <p className="text-sm md:text-base text-slate-700">
              You are granted a limited, non-exclusive, non-transferable license
              to use the platform solely for your internal business purposes in
              accordance with these Terms.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              11. Data &amp; Privacy
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              Our handling of personal data is described in our Privacy Policy,
              which is incorporated into these Terms by reference. By using
              CleanBid.ai, you also agree to the Privacy Policy available at{' '}
              <span className="font-mono">https://cleanbid.ai/privacy</span>.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              12. Disclaimers
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai is provided on an &quot;AS IS&quot; and &quot;AS
              AVAILABLE&quot; basis. We disclaim all warranties, express or
              implied, including but not limited to merchantability, fitness for a
              particular purpose, and non-infringement.
            </p>
            <p className="text-sm md:text-base text-slate-700">
              We do not guarantee:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Uninterrupted or error-free operation</li>
              <li>Delivery or receipt of any particular message</li>
              <li>Compatibility with all devices, networks, or carriers</li>
              <li>That defects will be corrected immediately</li>
            </ul>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              13. Limitation of Liability
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              To the maximum extent permitted by law, CleanBid.ai and its
              officers, employees, agents, and affiliates shall not be liable for:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>
                Indirect, incidental, special, punitive, or consequential
                damages
              </li>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or business interruption</li>
              <li>
                Any damages arising from or related to carrier behavior,
                third-party services, or regulatory enforcement caused by your
                actions
              </li>
            </ul>
            <p className="text-sm md:text-base text-slate-700">
              In no event shall our total liability arising out of or related to
              these Terms exceed the amount you have paid to CleanBid.ai in the
              twelve (12) months preceding the event giving rise to the claim.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              14. Indemnification
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              You agree to indemnify, defend, and hold harmless CleanBid.ai and
              its affiliates from and against any claims, liabilities, damages,
              losses, and expenses (including reasonable attorneys&apos; fees)
              arising out of or in any way connected with:
            </p>
            <ul className="list-disc pl-5 text-sm md:text-base text-slate-700 space-y-1">
              <li>Your use of the platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party right, law, or regulation</li>
              <li>Your misuse of messaging or communication tools</li>
            </ul>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              15. Governing Law
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              These Terms are governed by and construed in accordance with the
              laws of the State of Connecticut, USA, without regard to its
              conflict of law principles.
            </p>
          </section>

          <section className="space-y-4 mt-8">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              16. Changes to These Terms
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              We may update these Terms from time to time. The updated version
              will be posted at{' '}
              <span className="font-mono">https://cleanbid.ai/terms</span> with a
              revised &quot;Last updated&quot; date. Your continued use of
              CleanBid.ai after changes become effective constitutes your
              acceptance of the new Terms.
            </p>
          </section>

          <section className="space-y-4 mt-8 mb-6">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              17. Contact Us
            </h2>
            <p className="text-sm md:text-base text-slate-700">
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="text-sm md:text-base text-slate-700">
              CleanBid.ai <br />
              Email:{' '}
              <a
                href="mailto:support@cleanbid.ai"
                className="underline text-slate-900"
              >
                support@cleanbid.ai
              </a>
              <br />
              Website:{' '}
              <a
                href="https://cleanbid.ai"
                target="_blank"
                rel="noreferrer"
                className="underline text-slate-900"
              >
                cleanbid.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      <FooterSection />
    </>
  );
};

export default Terms;
